import { Button, Card, Checkbox, Divider, Flex, Form, FormProps, Switch, Tooltip } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// components
import ComponentHeader from 'components/componentHeader/componentHeader';
import StatusComponent from 'components/status/status';
import SwitchElement from 'components/switch/switch';
import BackButton from 'components/backButton/backButton';
import AnnouncementFormComponent from '../components/announcementForm';
import PageTitle from 'components/title/title';

// services

// styles
import smsIcon from 'assets/icons/sms.webp';
import { onCreateAnnouncement } from '../services/anouncementServices';
import { CreateAnnouncementPayload } from '../models/announcement';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import ActionCostComponent from '../../cost';
import { useDispatch } from 'react-redux';
import NotificationService from 'core/services/notification';
import { useNavigate } from 'react-router-dom';
import { subtractPoints } from 'stores/useCompanyStore';
import { onGetServiceCost } from '../../commonServices';
import { ServiceCost } from '../../commonModels';
import { isRTL } from 'core/services/language';

interface AnnouncementCreatePageProps {}

const AnnouncementCreatePage: FunctionComponent<AnnouncementCreatePageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [announcementForm] = Form.useForm();

	const [isLoading, setIsLoading] = useState(true);
	const [publicServiceCost, setPublicServiceCost] = useState<ServiceCost>();
	const [usersServiceCost, setUsersServiceCost] = useState<ServiceCost>();

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [englishAnnouncement, setEnglishAnnouncement] = useState(false);
	const [sendToShareholders, setSendToShareholders] = useState(false);
	const [agreement, setAgreement] = useState(false);

	useEffect(() => {
		getServiceCost();
	}, []);

	const getServiceCost = async () => {
		try {
			setIsLoading(true);

			const response = await Promise.all([onGetServiceCost('announcements'), onGetServiceCost('announcement_messages')]);

			setPublicServiceCost(response[0]?.success);
			setUsersServiceCost(response[1]?.success);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			navigate('/dashboard/actions/announcement');
		}
	};

	const onFinish: FormProps<CreateAnnouncementPayload>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			await onCreateAnnouncement(values);

			dispatch(
				subtractPoints(
					sendToShareholders && usersServiceCost?.payment_method == 'points'
						? usersServiceCost?.payment_details.points_required
						: !sendToShareholders && publicServiceCost?.payment_method == 'points'
						? publicServiceCost?.payment_details.points_required
						: 0,
				),
			);

			setIsSubmitting(false);

			navigate('/dashboard/actions/announcement');
			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.ANNOUNCEMENT_CREATED'));
		} catch (error) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton />

			{isLoading ? (
				<LoadingComponent />
			) : (
				<Form form={announcementForm} onFinish={onFinish} scrollToFirstError>
					<Card>
						<ComponentHeader title={t('ACTIONS.ANNOUNCEMENT')} subtitle={t('ACTIONS.FILL_TO_REVIEW')} />

						<div>
							<h5 style={{ fontSize: '1.1rem' }}>{t('ACTIONS.AUDIENCE')}</h5>
							<div className="mt-4">
								<Flex align="center" gap={10}>
									<h6 className="fw-medium mb-0">{t('ACTIONS.PUBLIC')}</h6>
									<StatusComponent text={t('ACTIONS.FREE_PUBLIC')} color="green" iconName="energy" iconHeight={15} iconWidth={10} small />
								</Flex>
								<p className="text-light">{t('ACTIONS.PUBLISH_TO_PUBLIC')}</p>
							</div>

							<div className="mt-2">
								<Flex gap={10}>
									<Form.Item name="notified_by_sms">
										<Switch
											disabled={!usersServiceCost?.can_access}
											style={{ marginTop: 5 }}
											onChange={(value) => {
												setSendToShareholders(value);
												announcementForm.setFieldsValue({ notified_by_sms: value });
											}}
										/>
									</Form.Item>
									<div>
										<Flex align="center" gap={15} style={{ minHeight: 30 }}>
											<h6 className="mb-0">{t('ACTIONS.NOTIFY_SHAREHOLDERS')}</h6>
											{sendToShareholders && (
												<StatusComponent
													text={
														usersServiceCost?.payment_method === 'usage'
															? t('ACTIONS.USAGE_CALCULATOR', {
																	cost: usersServiceCost?.payment_details?.usage_required,
																	remaining: usersServiceCost?.payment_details.usage_remaining,
															  })
															: usersServiceCost?.payment_method === 'points'
															? t('ACTIONS.TRANSACTION_CALCULATOR', {
																	shareholders: usersServiceCost?.cost_breakdown?.total_users,
																	transactions:
																		usersServiceCost?.cost_breakdown?.unit_cost_after_discount ?? usersServiceCost?.cost_breakdown?.unit_cost,
																	total: usersServiceCost?.cost_breakdown?.discount?.amount ?? usersServiceCost?.cost_breakdown?.total_cost,
															  })
															: usersServiceCost?.payment_method === 'combined'
															? isRTL()
																? usersServiceCost?.message?.ar
																: usersServiceCost?.message?.en
															: ''
													}
													color="green"
													iconName="energy"
													iconHeight={15}
													iconWidth={10}
													small
												/>
											)}
										</Flex>

										<p className="text-light mb-1">{t('ACTIONS.BROADCAST_ANNOUNCEMENT')}</p>

										{sendToShareholders && (
											<Flex gap={10} align="center" className="containerDiv p-2 w-max">
												<img src={smsIcon} style={{ width: '40', height: 40 }} />
												<div>
													<p className="fw-medium mb-1">{t('ACTIONS.COST_PER_SMS')}</p>
													<StatusComponent
														text={
															usersServiceCost?.payment_method === 'usage' ? (
																t('ACTIONS.TRANSACTIONS_PER_SMS')
															) : (
																<>
																	{usersServiceCost?.cost_breakdown?.unit_cost_after_discount && (
																		<span style={{ paddingInline: 5, fontWeight: 600, color: '#ff2c2c', textDecoration: 'line-through' }}>
																			{usersServiceCost?.cost_breakdown?.unit_cost}
																		</span>
																	)}
																	{t('ACTIONS.POINT_PER_SMS', {
																		point: usersServiceCost?.cost_breakdown?.unit_cost_after_discount ?? usersServiceCost?.cost_breakdown?.unit_cost,
																	})}
																</>
															)
														}
														color="green"
														small
														iconName="energy"
														iconHeight={15}
														iconWidth={10}
													/>
												</div>
											</Flex>
										)}
									</div>
								</Flex>
							</div>
						</div>

						<Divider />

						<ComponentHeader title={t('ACTIONS.ANNOUNCEMENT_DETAILS')} subtitle={t('ACTIONS.AWARE_THIS_CONTENT_BE_PUBLIC')} />

						<AnnouncementFormComponent language="ar" form={announcementForm} />

						<div className="mt-4 mb-3">
							<SwitchElement label={t('ACTIONS.ADD_ANNOUNCEMENT_IN_ENGLISH')} onChange={setEnglishAnnouncement} />
						</div>

						{englishAnnouncement && <AnnouncementFormComponent language="en" form={announcementForm} />}

						<Flex justify={sendToShareholders ? 'space-between' : 'flex-end'} align="center" gap={15} className="mt-5">
							{sendToShareholders && (
								<Checkbox className="compoundCheckbox" onChange={(e) => setAgreement(e.target.checked)}>
									<p className="fw-medium mb-0">{t('ACTIONS.CONFIRM_SEND_SHAREHOLDERS')}</p>
									<p className="mb-0">{t('ACTIONS.I_AGREE_TO_TERMS')}</p>
								</Checkbox>
							)}

							<Flex justify="flex-end" align="center" gap={15}>
								<ActionCostComponent serviceCost={sendToShareholders ? usersServiceCost! : publicServiceCost!} />

								<Tooltip title={sendToShareholders && !agreement ? t('ACTIONS.AGREE_FIRST') : ''} color="#EE4B2B">
									<Button type="primary" size="middle" htmlType="submit" disabled={sendToShareholders && !agreement} loading={isSubmitting}>
										{t('ACTIONS.SEND_FOR_REVIEW')}
									</Button>
								</Tooltip>
							</Flex>
						</Flex>
					</Card>
				</Form>
			)}
		</>
	);
};

export default AnnouncementCreatePage;
